import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/UserLayout';
import { api } from '../../utils/api';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from "../../contexts/ToastContext";
import ComprehensiveFormV2 from '../../components/ComprehensiveFormV2';
import AIMagicLoader from "../../components/AIMagicLoader";
import LiquidGlassStepper from "../../components/LiquidGlassStepper";
import ProgressModal from '../../components/ProgressModal';
import PdfPreview from "../../components/PdfPreview";

export default function NewApplication() {

  const navigate = useNavigate();
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [visaCategories, setVisaCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [visaTypes, setVisaTypes] = useState([]);
  const [visaFees, setVisaFees] = useState({});
  const [selectedVisaType, setSelectedVisaType] = useState(null);
  const [selectedVisaTypeDetails, setSelectedVisaTypeDetails] = useState(null);
  const [purposes, setPurposes] = useState([]);
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [availableCurrencies, setAvailableCurrencies] = useState(['INR', 'USD', 'EUR', 'GBP', 'AED']);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [numPassengers, setNumPassengers] = useState(1);
  const [currentPassengerIndex, setCurrentPassengerIndex] = useState(0);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderDone, setLoaderDone] = useState(false);
  const [extractedCount, setExtractedCount] = useState(0);
  const [passengerApplications, setPassengerApplications] = useState([]);
  const [extractedData, setExtractedData] = useState({});
  const [dataExtracted, setDataExtracted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [pmHeadline, setPmHeadline] = useState("Uploading & Processing…");
  const [pmSubline, setPmSubline] = useState("Please keep this tab open");
  const [pmPercent, setPmPercent] = useState(0);
  const [pmLogs, setPmLogs] = useState([]);
  const [pmDone, setPmDone] = useState(false);
  const pmPollRef = useRef(null);
  const pmLastBatchIdRef = useRef(null);
  const [pmSessionId, setPmSessionId] = useState(null);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const fileInputRefs = useRef({});
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    return () => stopProgressPolling();
  }, []);

  useEffect(() => {
    fetchVisaTypes();
  }, []);

  useEffect(() => {
    const passenger = passengerApplications[currentPassengerIndex];
    if (!passenger) return;
    const name = passenger.name?.trim() || "";
    if (name.length < 3) {
      setShowDocumentUpload(false);
      return;
    }
    const timer = setTimeout(() => setShowDocumentUpload(true), 400);
    return () => clearTimeout(timer);
  }, [passengerApplications[currentPassengerIndex]?.name]);

  const fetchVisaTypes = async () => {
    try {
      const visaRes = await api.getVisaTypes();

      // API returns array of categories with visa_types and purposes
      // Example: [{ category: "Tourist", visa_types: [...], purposes: [...] }]
      setVisaCategories(visaRes.data);

      // Optional: Fetch fee configs if needed
      try {
        const feeRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/fee-configs`);
        const feeMap = {};
        const currencySet = new Set();
        feeRes.data.forEach(fee => {
          if (fee.is_active) {
            if (!feeMap[fee.visa_type_id]) feeMap[fee.visa_type_id] = {};
            feeMap[fee.visa_type_id][fee.currency] = fee;
            currencySet.add(fee.currency);
          }
        });
        setVisaFees(feeMap);
        if (currencySet.size > 0) {
          const available = Array.from(currencySet);
          setAvailableCurrencies(available);
          if (!currencySet.has(selectedCurrency)) setSelectedCurrency(available[0]);
        }
      } catch (feeError) {
        console.log("Fee configs not available, using base fees from visa types");
      }
    } catch (error) {
      console.error(error);
      showToast("Failed to load visa types", "error");
    }
  };

  const createEmptyPassengerData = () => ({
    name: '',
    applicationId: null,
    applicationNumber: null,
    uploadedDocs: {}, // Files uploaded to S3
    tempDocs: {},     // Files selected but not uploaded yet
    passengerInfo: {
      // Basic Details
      passport_type: '',
      nationality: '',
      port_of_arrival: '',
      date_of_birth: '',
      email_id: '',
      re_enter_email_id: '',
      expected_date_of_arrival: '',
      visa_service: '',
      // Personal Details
      surname: '',
      given_name: '',
      name_changed: '',
      previous_surname: '',
      previous_given_name: '',
      gender: '',
      dob: '',
      town_city_of_birth: '',
      country_of_birth: '',
      citizenship_national_id: '',
      religion: '',
      identity_marks: '',
      educational_qualification: '',
      acquired_qualification: '',
      nationality_acquisition: '',
      prev_nationality: '',
      lived_2_years: '',
      passport_number: '',
      passport_place_of_issue: '',
      passport_date_of_issue: '',
      passport_date_of_expiry: '',
      other_passport: '',
      other_passport_country: '',
      other_passport_no: '',
      other_passport_doi: '',
      other_passport_poi: '',
      other_passport_nationality: '',
      // Present Address
      present_address_street: '',
      present_address_city: '',
      present_address_country: '',
      present_address_state: '',
      present_address_zip: '',
      present_phone: '',
      present_mobile: '',
      present_email: '',
      same_address_checkbox: false,
      // Permanent Address
      permanent_street: '',
      permanent_city: '',
      permanent_state: '',
      permanent_country: '',
      permanent_zip: '',
      permanent_phone: '',
      // Father Details
      father_name: '',
      father_nationality: '',
      father_prev_nationality: '',
      father_pob: '',
      father_cob: '',
      // Mother Details
      mother_name: '',
      mother_nationality: '',
      mother_prev_nationality: '',
      mother_pob: '',
      mother_cob: '',
      // Spouse Details
      marital_status: '',
      spouse_name: '',
      spouse_birth_place: '',
      spouse_nationality: '',
      spouse_birth_country: '',
      spouse_prev_nationality: '',
      // Pakistan Heritage Details
      pak_heritage: '',
      pak_heritage_details: '',
      type_of_visa: '',
      places_to_visit: '',
      // Hotel Details
      hotel_booking: '',
      hotel_resort_city: '',
      hotel_resort_name: '',
      // Military Details
      military_service: '',
      military_designation: '',
      military_organization: '',
      military_rank: '',
      military_posting: '',
      // Occupation Details
      occupation: '',
      designation: '',
      employer_name: '',
      employer_address: '',
      // employer_phone: '',
      past_occupation: '',
      // Company Details (for Business Visa)
      applicant_company_name: '',
      applicant_company_website: '',
      applicant_company_address_phone: '',
      nature_of_business_products: '',
      // Trip Details
      visa_duration: '',
      entries: '',
      arrival_port: '',
      exit_port: '',
      visited_india_before: '',
      prev_stay_address: '',
      cities_visited: '',
      last_visa_no: '',
      last_visa_type: '',
      last_visa_poi: '',
      last_visa_doi: '',
      permission_refused: '',
      refusal_details: '',
      countries_visited_10_years: '',
      // SAARC Details (updated structure)
      saarc_visit: '',
      saarc_visits: [], // Array of {country: '', year: '', number: ''}
      // Reference Details
      reference_india_name: '',
      reference_india_address: '',
      reference_india_phone: '',
      reference_home_name: '',
      reference_home_address: '',
      reference_home_phone: '',
      // Additional Questions
      arrested: '',
      arrested_details: '',
      deported: '',
      deported_details: '',
      human_trafficking: '',
      ht_details: '',
      cyber_crime: '',
      cyber_details: '',
      extremist_views: '',
      extremist_details: '',
      asylum: '',
      asylum_details: '',
      // Declaration
      declaration: false
    }
  });

  const handleStep1Next = async () => {
    if (!selectedCategory) {
      showToast("Please select a visa category", "error");
      return;
    }
    if (!selectedVisaType) {
      showToast("Please select a visa type", "error");
      return;
    }
    if (!selectedPurpose) {
      showToast("Please select a purpose", "error");
      return;
    }

    try {
      const response = await api.getVisaTypeDocuments(selectedVisaType);
      setRequiredDocuments(response.data);
      setStep(2);
    } catch {
      showToast("Failed to load document requirements", "error");
    }
  };

  const startProgressPolling = (sessionId) => {
    if (pmPollRef.current) {
      clearInterval(pmPollRef.current);
    }
    pmPollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/upload/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = res.data || {};
        const pct = Math.min(100, Math.max(0, data.progress || 0));
        setPmPercent(pct);
        if (data.message) setPmSubline(data.message);
        if (Array.isArray(data.logs)) setPmLogs(data.logs);

        if (data.status === "completed" || pct === 100) {
          setPmDone(true);
          clearInterval(pmPollRef.current);
          pmPollRef.current = null;
        }
        if (data.status === "error") {
          setPmHeadline("Processing failed");
          showToast(data.error || "Processing failed", "error");
          clearInterval(pmPollRef.current);
          pmPollRef.current = null;
        }
      } catch (err) {
        console.error(err);
        clearInterval(pmPollRef.current);
        pmPollRef.current = null;
      }
    }, 1000);
  };

  const stopProgressPolling = () => {
    if (pmPollRef.current) {
      clearInterval(pmPollRef.current);
      pmPollRef.current = null;
    }
  };

  const handleDeleteDocument = async (docTypeId, documentId) => {
    const currentPassenger = passengerApplications[currentPassengerIndex];
    const appId = currentPassenger?.applicationId;

    if (!appId) {
      showToast("Application not created yet", "error");
      return;
    }

    if (!documentId) {
      showToast("Document id missing", "error");
      return;
    }

    try {
      setLoading(true);
      await api.deleteDocument(documentId);

      const updated = [...passengerApplications];
      const existing = updated[currentPassengerIndex].uploadedDocs?.[docTypeId];

      if (Array.isArray(existing)) {
        updated[currentPassengerIndex].uploadedDocs[docTypeId] = existing.filter(
          (d) => d?.id !== documentId
        );
      } else {
        if (existing?.id === documentId) {
          updated[currentPassengerIndex].uploadedDocs[docTypeId] = [];
        }
      }

      setPassengerApplications(updated);
      showToast("Document deleted successfully", "success");
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to delete document", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTempDocument = (docTypeId, fileIndex) => {
    const updated = [...passengerApplications];
    const tempDocs = updated[currentPassengerIndex].tempDocs;

    if (Array.isArray(tempDocs[docTypeId])) {
      tempDocs[docTypeId].splice(fileIndex, 1);
      if (tempDocs[docTypeId].length === 0) {
        delete tempDocs[docTypeId];
      }
    }

    setPassengerApplications(updated);
    showToast("File removed", "info");
  };

  const handlePreviewTempFile = (file) => {
    // Create a temporary URL for the file
    const fileUrl = URL.createObjectURL(file);
    setPreviewFile({
      url: fileUrl,
      name: file.name,
      type: file.type,
      isTemp: true
    });
    setPreviewOpen(true);
  };

  const closePreview = () => {
    if (previewFile?.isTemp && previewFile?.url) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
    setPreviewOpen(false);
  };

  const handleBulkZipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".zip")) {
      showToast("Please upload a .zip file", "error");
      e.target.value = "";
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const startRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/start-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { session_id } = startRes.data || {};
      if (!session_id) {
        showToast("Could not start progress session", "error");
        setLoading(false);
        e.target.value = "";
        return;
      }
      setPmSessionId(session_id);

      setPmHeadline("Uploading & Processing…");
      setPmSubline("Please keep this tab open");
      setPmPercent(0);
      setPmLogs([]);
      setPmDone(false);
      setPmOpen(true);
      startProgressPolling(session_id);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("visa_type_id", selectedVisaType);
      formData.append("session_id", session_id);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/ingest-zip`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { applications, batch_id } = res.data || {};
      pmLastBatchIdRef.current = batch_id;
      if (!applications || applications.length === 0) {
        setPmHeadline("No applications detected");
        showToast("No valid applications found in ZIP", "error");
        return;
      }

      const finalBatchId = batch_id || `TEMP_BATCH_${Date.now()}`;
      const goToReview = () =>
        setTimeout(() => navigate(`/bulk-review?batch=${finalBatchId}`), 800);

      if (pmDone) goToReview();
      else {
        const unwatch = setInterval(() => {
          if (pmDone) {
            clearInterval(unwatch);
            goToReview();
          }
        }, 200);
      }
    } catch (error) {
      console.error(error);
      setPmHeadline("Processing failed");
      showToast(error.response?.data?.detail || "Bulk upload failed, please try again", "error");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const handleStep2Next = () => {
    if (!numPassengers || numPassengers < 1 || numPassengers > 50) {
      showToast("Please enter number of passengers (1-50)", "error");
      return;
    }
    const apps = Array(parseInt(numPassengers, 10)).fill(null).map(() => createEmptyPassengerData());
    setPassengerApplications(apps);
    setCurrentPassengerIndex(0);
    setStep(3);
  };

  const handleTempDocumentAdd = (docTypeId, file) => {
    const updated = [...passengerApplications];
    const tempDocs = updated[currentPassengerIndex].tempDocs;

    if (!Array.isArray(tempDocs[docTypeId])) {
      tempDocs[docTypeId] = [];
    }

    tempDocs[docTypeId].push(file);
    setPassengerApplications(updated);

    showToast("File added. Click 'Extract Data' to upload & extract.", "success");
  };

  const normalizeFieldName = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  };

  const handleExtractData = async () => {
    const currentPassenger = passengerApplications[currentPassengerIndex];

    // Calculate total files (uploaded + temp)
    const uploadedCount = Object.values(currentPassenger.uploadedDocs || {})
      .reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    const tempCount = Object.values(currentPassenger.tempDocs || {})
      .reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);

    if (uploadedCount === 0 && tempCount === 0) {
      showToast("Please select at least one document", "error");
      return;
    }

    /* ------------------------------------------------
     * START LOADER IMMEDIATELY
     * ------------------------------------------------ */
    setShowLoader(true);
    setLoaderDone(false);
    setExtractedCount(0);

    try {
      /* ------------------------------------------------
       * STEP 1: CREATE APPLICATION (SILENTLY IN BACKGROUND)
       * ------------------------------------------------ */
      let appId = currentPassenger.applicationId;
      let applicationNumber = currentPassenger.applicationNumber;

      if (!appId) {
        const response = await api.createApplication({
          visa_type_id: selectedVisaType,
          num_passengers: 1,
          purpose_code: selectedPurpose,
        });
        appId = response.data.id;
        applicationNumber = response.data.application_number || response.data.id;

        const updated = [...passengerApplications];
        updated[currentPassengerIndex].applicationId = appId;
        updated[currentPassengerIndex].applicationNumber = applicationNumber;
        setPassengerApplications(updated);
      }

      const token = localStorage.getItem("token");
      const tempDocs = currentPassenger.tempDocs || {};

      /* ------------------------------------------------
       * STEP 2: BULK UPLOAD ALL PENDING FILES TO S3
       * ------------------------------------------------ */
      if (Object.keys(tempDocs).length > 0) {
        const uploadFormData = new FormData();
        uploadFormData.append("application_id", appId);

        Object.entries(tempDocs).forEach(([docTypeId, files]) => {
          files.forEach((file) => {
            uploadFormData.append("document_type_ids", docTypeId);
            uploadFormData.append("files", file);
          });
        });

        const uploadRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/bulk-upload`,
          uploadFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const uploadedDocs = uploadRes.data;

        if (!Array.isArray(uploadedDocs) || uploadedDocs.length === 0) {
          throw new Error("No documents were uploaded");
        }

        /* ------------------------------------------------
         * STEP 3: MOVE uploaded docs into uploadedDocs state
         * ------------------------------------------------ */
        const updated = [...passengerApplications];
        const passenger = updated[currentPassengerIndex];

        uploadedDocs.forEach((doc) => {
          if (!Array.isArray(passenger.uploadedDocs[doc.document_type_id])) {
            passenger.uploadedDocs[doc.document_type_id] = [];
          }
          passenger.uploadedDocs[doc.document_type_id].push(doc);
        });

        passenger.tempDocs = {}; // Clear temp files
        setPassengerApplications(updated);
      }

      /* ------------------------------------------------
       * STEP 4: TRIGGER EXTRACTION (BACKEND READS DB FILES)
       * ------------------------------------------------ */
      const extractRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/extract-and-map?application_id=${appId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fields = extractRes.data?.mapped || {};
      setExtractedData(fields);
      setDataExtracted(true);

      const extractedNonEmpty = Object.values(fields).filter(
        (v) => v !== null && v !== undefined && v !== ""
      ).length;
      setExtractedCount(extractedNonEmpty);

      /* ------------------------------------------------
       * STEP 5: MAP EXTRACTED DATA INTO FORM STATE
       * ------------------------------------------------ */
      const updated = [...passengerApplications];
      const info = updated[currentPassengerIndex].passengerInfo;

      const dateFields = [
        "date_of_birth",
        "dob",
        "passport_date_of_issue",
        "passport_date_of_expiry",
        "other_passport_doi",
        "other_passport_poi",
        "last_visa_doi",
        "expected_date_of_arrival",
      ];

      Object.entries(fields).forEach(([fieldName, value]) => {
        if (!value) return;

        const key = normalizeFieldName(fieldName);
        if (!(key in info)) return;

        if (key === "gender") {
          const g = String(value).toUpperCase();
          info.gender =
            g === "M" || g === "MALE"
              ? "Male"
              : g === "F" || g === "FEMALE"
                ? "Female"
                : "Other";
          return;
        }

        if (dateFields.includes(key)) {
          info[key] = /^\d{4}\.\d{2}\.\d{2}$/.test(value)
            ? value.replaceAll(".", "-")
            : value;
          return;
        }

        if (key === "declaration" || key === "same_address_checkbox") {
          info[key] = ["yes", "true", "1", "y"].includes(
            String(value).toLowerCase()
          );
          return;
        }

        info[key] = value;
      });

      /* ------------------------------------------------
       * STEP 6: DEFAULTS + ADDRESS COPY
       * ------------------------------------------------ */
      info.passport_type ||= "Regular";
      info.gender ||= "Male";
      info.name_changed = "No";
      info.other_passport = "No";
      info.pak_heritage = "No";
      info.visited_india_before = "No";
      info.saarc_visit = "No";
      info.nationality_acquisition ||= "By Birth";
      info.marital_status ||= "Single";
      info.military_service ||= "No";
      info.hotel_booking ||= "No";
      info.same_address_checkbox = true;
      info.permanent_street = info.present_address_street || "";
      info.permanent_city = info.present_address_city || "";
      info.permanent_state = info.present_address_state || "";
      info.permanent_zip = info.present_address_zip || "";
      info.permanent_country = info.present_address_country || "";

      setPassengerApplications(updated);

      setLoaderDone(true);
      showToast("Documents uploaded & data extracted successfully", "success");
      setTimeout(() => setShowLoader(false), 1200);
    } catch (error) {
      console.error(error);
      setShowLoader(false);
      showToast(
        error.response?.data?.detail ||
        error.message ||
        "Extraction failed",
        "error"
      );
    }
  };

  const handleDocumentsNext = async () => {
    const currentPassenger = passengerApplications[currentPassengerIndex];

    if (!currentPassenger) {
      showToast("Passenger data not found", "error");
      return;
    }

    // Check mandatory documents: uploaded + temp
    const missingDocs = requiredDocuments.filter((doc) => {
      if (!doc.is_mandatory) return false;

      const uploadedCount =
        Array.isArray(currentPassenger.uploadedDocs?.[doc.id])
          ? currentPassenger.uploadedDocs[doc.id].length
          : 0;

      const tempCount =
        Array.isArray(currentPassenger.tempDocs?.[doc.id])
          ? currentPassenger.tempDocs[doc.id].length
          : 0;

      return uploadedCount + tempCount === 0;
    });

    if (missingDocs.length > 0) {
      showToast(
        `Please upload all required documents: ${missingDocs
          .map((d) => d.name)
          .join(", ")}`,
        "error"
      );
      return;
    }

    // Trigger extraction if not done yet
    if (!dataExtracted) {
      showToast("Extracting data from documents...", "info");
      await handleExtractData();
    }

    // Move to form step
    setStep(5);
  };

  const updatePassengerInfo = (field, value) => {
    const updated = [...passengerApplications];
    updated[currentPassengerIndex].passengerInfo[field] = value;
    updated[currentPassengerIndex].passengerInfo.full_name = updated[currentPassengerIndex].name;
    setPassengerApplications(updated);
  };

  const isFieldEmpty = () => false;
  const getFieldClassName = () => '';

  const handlePassengerInfoNext = async () => {
    const currentPassenger = passengerApplications[currentPassengerIndex];
    const info = currentPassenger.passengerInfo;

    try {
      setLoading(true);
      await api.updateApplication(currentPassenger.applicationId, {
        passengers: [info],
        status: 'draft'
      });
      showToast(`Passenger ${currentPassengerIndex + 1} information saved`, "success");

      if (currentPassengerIndex < numPassengers - 1) {
        setCurrentPassengerIndex(currentPassengerIndex + 1);
        setDataExtracted(false);
        setExtractedData({});
        setStep(3);
        showToast(`Now entering details for Passenger ${currentPassengerIndex + 2}`, "info");
      } else {
        setStep(6);
      }
    } catch {
      showToast("Failed to save passenger information", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAll = async () => {
    try {
      setLoading(true);
      for (let i = 0; i < passengerApplications.length; i++) {
        const passenger = passengerApplications[i];
        await api.updateApplication(passenger.applicationId, {
          passengers: [passenger.passengerInfo],
          status: 'Application Received, Pending for Review'
        });
      }
      showToast(`All ${numPassengers} applications submitted successfully!`, "success");
      navigate('/dashboard');
    } catch {
      showToast("Failed to submit applications", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------ Step renders ------------------------------ */
  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Step 1: Select Visa Category, Type & Purpose</h3>

      {/* Step 1a: Select Category */}
      <div className="space-y-4">
        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-3">Select Visa Category</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {visaCategories.map((categoryData) => (
              <div
                key={categoryData.category}
                onClick={() => {
                  setSelectedCategory(categoryData.category);
                  setVisaTypes(categoryData.visa_types || []);
                  setPurposes(categoryData.purposes || []);
                  setSelectedVisaType(null);
                  setSelectedPurpose(null);
                  setSelectedVisaTypeDetails(null);
                }}
                data-testid={`visa-category-${categoryData.category}`}
                className={`p-6 border-2 rounded-lg cursor-pointer transition-all text-center ${selectedCategory === categoryData.category
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
                  }`}
              >
                <div className="text-4xl mb-3">
                  {categoryData.category === 'Tourist' && '✈️'}
                  {categoryData.category === 'Business' && '💼'}
                  {categoryData.category === 'Student' && '🎓'}
                  {categoryData.category === 'Medical' && '🏥'}
                  {categoryData.category === 'Conference' && '🎤'}
                  {!['Tourist', 'Business', 'Student', 'Medical', 'Conference'].includes(categoryData.category) && '📋'}
                </div>
                <h4 className="font-semibold text-gray-900 text-lg">{categoryData.category} Visa</h4>

              </div>
            ))}
          </div>
        </div>

        {/* Step 1b: Select Visa Type (shown after category selection) */}
        {selectedCategory && visaTypes.length > 0 && (
          <div className="slide-down">
            <h4 className="text-lg font-medium text-gray-800 mb-3">Select Visa Type</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visaTypes.map((visa) => (
                <div
                  key={visa.id}
                  onClick={() => {
                    setSelectedVisaType(visa.id);
                    setSelectedVisaTypeDetails(visa);
                    setSelectedPurpose(null);
                  }}
                  data-testid={`visa-type-${visa.id}`}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedVisaType === visa.id
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                    }`}
                >
                  <h4 className="font-semibold text-gray-900">{visa.name}</h4>
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <p>⏱️ Processing: {visa.processing_time} days</p>
                    <p>📅 Validity: {visa.validity} days</p>

                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1c: Select Purpose (shown after visa type selection) */}
        {selectedVisaType && purposes.length > 0 && (
          <div className="slide-down">
            <h4 className="text-lg font-medium text-gray-800 mb-3">Select Purpose of Visit</h4>
            <div className="space-y-2">
              {purposes.map((purposeData) => (
                <div
                  key={purposeData.purpose_code}
                  onClick={() => setSelectedPurpose(purposeData.purpose_code)}
                  data-testid={`purpose-${purposeData.purpose_code}`}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedPurpose === purposeData.purpose_code
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPurpose === purposeData.purpose_code
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                      }`}>
                      {selectedPurpose === purposeData.purpose_code && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 leading-relaxed">{purposeData.purpose}</p>

                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Button
        onClick={handleStep1Next}
        data-testid="step1-next-btn"
        disabled={!selectedCategory || !selectedVisaType || !selectedPurpose}
        className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        Next
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Step 2: Number of Passengers</h3>
      <div>
        <Label htmlFor="numPassengers">How many passengers? (1-50)</Label>
        <Input
          id="numPassengers"
          data-testid="num-passengers-input"
          type="number"
          min="1"
          max="50"
          value={numPassengers}
          onChange={(e) => setNumPassengers(parseInt(e.target.value, 10))}
          className="mt-1 max-w-xs"
        />
        <p className="text-sm text-gray-500 mt-2">Each passenger will have a separate application</p>
      </div>
      <div className="flex space-x-4">
        <Button onClick={() => setStep(1)} variant="outline" data-testid="step2-back-btn">Back</Button>
        <Button onClick={handleStep2Next} data-testid="step2-next-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          Next
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">
          Passenger {currentPassengerIndex + 1} of {numPassengers}
        </h3>
        <p className="text-sm text-gray-600 mt-2">Enter passenger name</p>
      </div>

      <div>
        <Label htmlFor="passengerName">Passenger Name *</Label>
        <Input
          id="passengerName"
          type="text"
          value={passengerApplications[currentPassengerIndex]?.name || ''}
          onChange={(e) => {
            const updated = [...passengerApplications];
            updated[currentPassengerIndex].name = e.target.value;
            setPassengerApplications(updated);
          }}
          placeholder="Enter full name as per passport"
          className="mt-1"
        />
      </div>

      {showDocumentUpload && (
        <div className="slide-down mt-6">
          {renderStep4()}
        </div>
      )}

      <div className="flex space-x-4">
        <Button
          onClick={() => {
            if (currentPassengerIndex > 0) setCurrentPassengerIndex(currentPassengerIndex - 1);
            else setStep(2);
          }}
          variant="outline"
        >
          Back
        </Button>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const currentPassenger = passengerApplications[currentPassengerIndex];
    if (!currentPassenger) return null;

    // Calculate total files (uploaded + temp)
    const uploadedCount = Object.values(currentPassenger?.uploadedDocs || {})
      .reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    const tempCount = Object.values(currentPassenger?.tempDocs || {})
      .reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    const totalFiles = uploadedCount + tempCount;

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            Upload Documents for {currentPassenger?.name}
          </h3>
          <p className="text-sm text-gray-600 mt-2">
            Passenger {currentPassengerIndex + 1} of {numPassengers}
          </p>
        </div>

        <div className="space-y-4">
          {requiredDocuments.map((doc) => {
            const uploadedFiles = currentPassenger?.uploadedDocs?.[doc.id] || [];
            const tempFiles = currentPassenger?.tempDocs?.[doc.id] || [];
            const hasFiles = uploadedFiles.length > 0 || tempFiles.length > 0;

            return (
              <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-6">
                  {/* LEFT: Document Info */}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {doc.name}
                      {doc.is_mandatory && (
                        <span className="text-red-600 ml-1">*</span>
                      )}
                    </h4>

                    <p className="text-sm text-gray-600 mt-1">
                      {doc.description}
                    </p>

                    <p className="text-xs text-gray-500 mt-2">
                      Allowed: {doc.allowed_formats.join(', ')} | Max: {doc.max_size_mb}MB
                    </p>

                    {/* Documents Table */}
                    {hasFiles && (
                      <div className="mt-4">
                        <h5 className="text-sm font-semibold text-gray-800 mb-2">
                          Selected Documents
                        </h5>

                        <div className="overflow-x-auto border border-gray-200 rounded-md">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-blue-600">
                                  File Name
                                </th>
                                <th className="px-3 py-2 text-right font-medium text-blue-600">
                                  Actions
                                </th>
                              </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200 bg-white">
                              {/* Uploaded files (already in S3) */}
                              {uploadedFiles.map((file) => (
                                <tr key={file.id}>
                                  <td className="px-3 py-2 text-gray-800 truncate max-w-[400px]">
                                    {file.file_name}
                                  </td>
                                  <td className="px-3 py-2 text-right space-x-3">
                                    <a
                                      href={file.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-600 hover:underline"
                                    >
                                      View
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteDocument(doc.id, file.id)}
                                      className="text-red-600 hover:underline"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}

                              {/* Temp files (not uploaded yet) */}
                              {tempFiles.map((file, idx) => (
                                <tr key={`temp-${idx}`}>
                                  <td className="px-3 py-2 text-gray-800 truncate max-w-[400px]">
                                    {file.name}
                                  </td>
                                  <td className="px-3 py-2 text-right space-x-3">
                                    <button
                                      type="button"
                                      onClick={() => handlePreviewTempFile(file)}
                                      className="text-blue-600 hover:underline"
                                    >
                                      View
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteTempDocument(doc.id, idx)}
                                      className="text-red-600 hover:underline"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT: Upload Button */}
                  <div className="shrink-0">
                    <input
                      type="file"
                      ref={(el) => {
                        if (el) {
                          fileInputRefs.current[`${doc.id}-${currentPassengerIndex}`] = el;
                        }
                      }}
                      className="hidden"
                      accept={doc.allowed_formats.map((f) => `.${f}`).join(',')}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleTempDocumentAdd(doc.id, e.target.files[0]);
                        }
                        e.target.value = '';
                      }}
                    />

                    {(() => {
                      const isMulti = !!doc.allow_multiple_uploads;
                      const totalFilesForDoc = uploadedFiles.length + tempFiles.length;

                      // SINGLE upload - hide button if any file exists (uploaded or temp)
                      if (!isMulti && totalFilesForDoc > 0) {
                        return null;
                      }

                      // Show button for multi-upload or if no files yet
                      const buttonText = totalFilesForDoc > 0 ? 'Add More' : 'Choose File';

                      return (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            const key = `${doc.id}-${currentPassengerIndex}`;
                            fileInputRefs.current[key]?.click();
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {buttonText}
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Extract Data Button - Show when files are present */}
        {totalFiles > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex flex-col space-y-3">
              <div>
                <h4 className="font-medium text-gray-900 mb-1">
                  📄 Ready to Process Documents
                </h4>
                {dataExtracted ? (
                  <p className="text-sm text-green-600">
                    ✓ All documents processed! {extractedCount} fields extracted.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">
                    {tempCount > 0 && (
                      <>Click "Extract Data" to upload and process all documents.</>
                    )}
                  </p>
                )}
              </div>

              {!dataExtracted && (
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={handleDocumentsNext}
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="extract-data-btn"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>Extract Data</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep5 = () => {
    const currentPassenger = passengerApplications[currentPassengerIndex];
    const info = currentPassenger?.passengerInfo || {};

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            Passenger Information: {currentPassenger?.name}
          </h3>
        </div>

        <ComprehensiveFormV2
          info={info}
          updateField={updatePassengerInfo}
          dataExtracted={dataExtracted}
          getFieldClassName={getFieldClassName}
          isFieldEmpty={isFieldEmpty}
          visaType={selectedVisaTypeDetails}
        />

        <div className="flex space-x-4">
          <Button onClick={() => setStep(4)} variant="outline">Back</Button>
          <Button onClick={handlePassengerInfoNext} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
            {loading
              ? 'Saving...'
              : (currentPassengerIndex < numPassengers - 1
                ? `Next Passenger (${currentPassengerIndex + 2}/${numPassengers})`
                : 'Review All Applications')}
          </Button>
        </div>
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Review & Submit All Applications</h3>
      <div className="space-y-4">
        {passengerApplications.map((passenger, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="font-medium text-gray-900 mb-2">
              Passenger {index + 1}: {passenger.name}
            </h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>Application ID: {passenger.applicationId}</p>
              <p>Passport: {passenger.passengerInfo.passport_number}</p>
              {/* <p>Email: {passenger.passengerInfo.email_id}</p> */}
              <p>Documents: {Object.keys(passenger.uploadedDocs).length} uploaded</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            id="declaration"
            checked={declarationChecked}
            onCheckedChange={(checked) => setDeclarationChecked(checked === true)}
          />

          <Label
            htmlFor="declaration"
            className="text-sm leading-snug cursor-pointer"
          >
            I hereby declare that all information provided for all passengers is true
            and correct to the best of my knowledge.
          </Label>
        </div>
      </div>

      <div className="flex space-x-4">
        <Button onClick={() => { setCurrentPassengerIndex(numPassengers - 1); setStep(5); }} variant="outline">
          Back
        </Button>
        <Button onClick={handleSubmitAll} disabled={loading || !declarationChecked} className="bg-green-600 hover:bg-green-700 text-white">
          {loading ? 'Submitting...' : `Submit All ${numPassengers} Applications`}
        </Button>
      </div>
    </div>
  );

  /* --------------------------------- Render -------------------------------- */
  return (
    <UserLayout>
      {showLoader && (
        <AIMagicLoader
          isDone={loaderDone}
          extractedCount={extractedCount}
          onClose={() => setShowLoader(false)}
        />
      )}

      {/* Preview Modal for Temp Files */}
      {previewOpen && previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={closePreview}
        >
          <div
            className="relative bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">
                {previewFile.name}
              </h3>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {previewFile.type.startsWith("image/") ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  className="max-w-full h-auto mx-auto rounded"
                />
              ) : previewFile.type === "application/pdf" ? (
                <PdfPreview fileUrl={previewFile.url} />
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">
                    Preview not available for this file type
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {previewFile.name}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ProgressModal
        open={pmOpen}
        onClose={() => {
          setPmOpen(false);
          stopProgressPolling();
        }}
        headline={pmHeadline}
        subline={pmSubline}
        percent={pmPercent}
        logs={pmLogs}
        done={pmDone}
        onDoneClose={() => {
          const batchId = pmLastBatchIdRef.current;
          if (batchId) {
            navigate(`/bulk-review?batch=${batchId}`);
          }
        }}
      />

      <LiquidGlassStepper step={step} />

      <div className="pt-24" />

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}
        </div>
      </div>
    </UserLayout>
  );
}